import memoizeOne from "memoize-one"

export const isLocal = (host: string) =>
  /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}(:[0-9]{1,5})?$/.test(host) ||
  /^localhost(:[0-9]{1,5})?$/.test(host) ||
  /^[^\.]+$/.test(host)

const vercelGitRegex = /^web-app-git-([^/.]+)-dashfuel\.vercel\.app$/
const vercelPreviewRegex = /^web-(\w+)-dashfuel\.vercel\.app$/
/**
 * Determines if a host is a Vercel preview or git deployment
 * @param host - The host string to check
 * @returns True if the host is a Vercel preview or git deployment
 */
export const isVercelPreviewOrGit = (host: string): boolean =>
  vercelGitRegex.test(host) || vercelPreviewRegex.test(host)

type hostInfo = {
  env: string
  tenant: string
  logoutUrl: string
}

export const parseHost = (host: string): hostInfo => {
  const overrideEnv = process.env.REACT_APP_OVERRIDE_ENV
  const overrideTenant = process.env.REACT_APP_OVERRIDE_TENANT
  const tenant = overrideTenant || "dashfuel"
  const isLocalEnv = isLocal(host)

  const getLogoutUrl = (env: string): string => {
    if (isLocalEnv) {
      return `http://localhost:3000/`
    }
    // When in login subdomain redirect back to root without /logout
    if (host.startsWith("login.")) {
      return `https://${host}`
    }
    const envSuffix = env === "prod" ? "" : env
    return `https://login.getdashfuel${envSuffix}.com/logout`
  }

  if (overrideEnv && overrideTenant) {
    return {
      env: overrideEnv,
      tenant: overrideTenant,
      logoutUrl: getLogoutUrl(overrideEnv),
    }
  }

  const dashfuelEnv = host.match(/^([^/.]+)\.getdashfuel([^/.]*)\.com$/)
  if (dashfuelEnv) {
    const env = dashfuelEnv[2] || "prod"
    return {
      env,
      tenant: overrideTenant || dashfuelEnv[1],
      logoutUrl: getLogoutUrl(env),
    }
  }

  const vercelProd = host.match(/^(?:web-app-)?dashfuel\.vercel\.app$/)
  if (vercelProd)
    return { env: "prod", tenant, logoutUrl: getLogoutUrl("prod") }

  const vercelEnv = host.match(/^web-app-env-([^/.]+)-dashfuel\.vercel\.app$/)
  if (vercelEnv) {
    const envName = vercelEnv[1]
    if (envName === "dev")
      return { env: "dev", tenant, logoutUrl: getLogoutUrl("dev") }
    if (envName === "staging")
      return { env: "staging", tenant, logoutUrl: getLogoutUrl("staging") }
    if (envName === "production")
      return { env: "prod", tenant, logoutUrl: getLogoutUrl("prod") }
    if (envName === "dashfuelprod")
      return {
        env: "prod",
        tenant: "dashfuel",
        logoutUrl: getLogoutUrl("prod"),
      }
    throw Error(`Unknown vercel env ${envName}`)
  }

  const vercelGit = host.match(vercelGitRegex)
  if (vercelGit) {
    const branchName = vercelGit[1]
    if (branchName === "main") {
      return {
        env: "prod",
        tenant: "dashfuel",
        logoutUrl: getLogoutUrl("prod"),
      }
    }
    return { env: "dev", tenant, logoutUrl: getLogoutUrl("dev") }
  }

  const vercelPreview = host.match(vercelPreviewRegex)
  if (vercelPreview)
    return { env: "dev", tenant, logoutUrl: getLogoutUrl("dev") }

  if (isLocalEnv) return { env: "dev", tenant, logoutUrl: getLogoutUrl("dev") }

  throw Error(`Unknown host "${host}"`)
}

export const getHostInfo = (() => {
  const f = memoizeOne(parseHost)
  return () => f(window.location.host)
})()

export const isProd = getHostInfo().env === "prod"

export const getApiUrl = () => {
  const overrideApiUrl = process.env.REACT_APP_OVERRIDE_API_URL
  if (overrideApiUrl) return overrideApiUrl

  const { env, tenant } = getHostInfo()
  const baseDomain = `getdashfuel${env === "prod" ? "" : env}.com`
  return `https://${tenant}.api.${baseDomain}/api/`
}

export const getAuth0Params = () => {
  const overrideAuth0Domain = process.env.REACT_APP_OVERRIDE_AUTH0_DOMAIN
  const overrideAuth0ClientId = process.env.REACT_APP_OVERRIDE_AUTH0_CLIENT_ID
  if (overrideAuth0Domain && overrideAuth0ClientId) {
    return {
      domain: overrideAuth0Domain,
      clientId: overrideAuth0ClientId,
    }
  }

  const { env } = getHostInfo()

  switch (env) {
    case "local":
    case "dev":
      return {
        domain: "https://auth0.getdashfueldev.com",
        clientId: "uREQYnV9Xpmng3ggRppUSHbAvaHkPS5V",
      }
    case "staging":
      return {
        domain: "https://auth0.getdashfuelstaging.com",
        clientId: "67bz5PN3Ds1CAPNBWpRfm3kztlIoH2Bm",
      }
    case "prod":
      return {
        domain: "https://auth0.getdashfuel.com",
        clientId: "FxlllcOs4j3iO3RHCyCEZpzJ4cL1tJPJ",
      }
    default:
      throw Error(`Unknown env "${env}"`)
  }
}
